import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, X, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";

const EMPTY = {
  ragione_sociale: "",
  email: "",
  telefono: "",
  oggetto: "Richiesta preventivo",
  descrizione_lavori: "",
};

export default function RichiesteSubappaltatoriTab({ cantiereId, onOpenSubappalti }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });

  const [ricercaFornitore, setRicercaFornitore] = useState("");

  const { data: fornitori = [], isLoading: caricamentoFornitori } = useQuery({
    queryKey: ["fornitori-stats"],
    queryFn: () => api.get("/entita/fornitori/stats").then((r) => r.data),
    enabled: open,
  });

  const fornitoriFiltrati = (Array.isArray(fornitori) ? fornitori : [])
    .filter((f) => {
      const q = ricercaFornitore.toLowerCase().trim();
      return q && (
        (f.ragione_sociale || "").toLowerCase().includes(q) ||
        (f.partita_iva || "").toLowerCase().includes(q)
      );
    })
    .slice(0, 20);

  const selezionaFornitore = (f) => {
    setForm((precedente) => ({
      ...precedente,
      ragione_sociale: f.ragione_sociale || "",
      email: f.email || "",
      telefono: f.telefono || "",
    }));
    setRicercaFornitore("");
    toast.success("Dati compilati dall'Agenda Fornitori");
  };


  const { data: richieste = [], isLoading } = useQuery({
    queryKey: ["richieste-subappaltatori", cantiereId],
    queryFn: () =>
      api.get("/richieste-subappaltatori", {
        params: { cantiere_id: cantiereId },
      }).then((r) => r.data),
    enabled: !!cantiereId,
  });

  const save = useMutation({
    mutationFn: (data) =>
      api.post("/richieste-subappaltatori", {
        ...data,
        cantiere_id: cantiereId,
      }),
    onSuccess: () => {
      toast.success("Richiesta di preventivo salvata");
      qc.invalidateQueries({
        queryKey: ["richieste-subappaltatori", cantiereId],
      });
      setOpen(false);
      setForm({ ...EMPTY });
    },
    onError: (e) =>
      toast.error(e.response?.data?.detail || "Errore nel salvataggio"),
  });

  const converti = useMutation({
    mutationFn: (richiestaId) =>
      api.post(`/richieste-subappaltatori/${richiestaId}/converti`),
    onSuccess: () => {
      toast.success("Subappalto creato come bozza");
      qc.invalidateQueries({ queryKey: ["richieste-subappaltatori", cantiereId] });
      qc.invalidateQueries({ queryKey: ["subappalti"] });
      onOpenSubappalti?.();
    },
    onError: (e) =>
      toast.error(e.response?.data?.detail || "Errore nella conversione"),
  });

  const confermaConversione = (r) => {
    if (converti.isPending || r.subappalto_id) return;
    if (window.confirm(`Trasformare la richiesta di ${r.ragione_sociale} in un subappalto in bozza?`)) {
      converti.mutate(r.richiesta_id);
    }
  };

  const shareWhatsApp = (r) => {
    const telefono = (r.telefono || "").replace(/\D/g, "");
    const messaggio = [
      r.oggetto || "Richiesta preventivo",
      "",
      `Spett.le ${r.ragione_sociale},`,
      "",
      r.descrizione_lavori,
      "",
      "Cordiali saluti",
      "Ethos Costruzioni",
    ].join("\n");

    if (!telefono) {
      toast.error("Inserisci il telefono del subappaltatore");
      return;
    }

    window.open(
      `https://wa.me/${telefono}?text=${encodeURIComponent(messaggio)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="space-y-5" data-testid="richieste-subappaltatori-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold">
            Richieste a subappaltatore
          </h2>
          <p className="text-sm text-slate-500">
            Richieste di preventivo per lavori da affidare a ditte esterne.
          </p>
        </div>

        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-sm"
        >
          <Plus size={17} /> Richiesta preventivo
        </button>
      </div>

      {isLoading ? (
        <p>Caricamento richieste...</p>
      ) : richieste.length === 0 ? (
        <div className="border border-dashed rounded p-8 text-center text-slate-500">
          Nessuna richiesta di preventivo presente.
        </div>
      ) : (
        <div className="space-y-3">
          {richieste.map((r) => (
            <div key={r.richiesta_id} className="border rounded p-4 bg-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{r.ragione_sociale}</h3>
                  <p className="text-sm text-slate-500">{r.oggetto}</p>
                  <span className="text-xs text-slate-500">
                    Stato: {r.stato || "bozza"}
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => confermaConversione(r)}
                    disabled={converti.isPending || !!r.subappalto_id}
                    className="px-3 py-2 border rounded text-sm disabled:opacity-50"
                  >
                    {r.subappalto_id ? "Convertita" : converti.isPending ? "Attendere..." : "Trasforma in subappalto"}
                  </button>
                  {r.email && (
                    <a
                      href={`mailto:${r.email}?subject=${encodeURIComponent(r.oggetto)}&body=${encodeURIComponent(r.descrizione_lavori)}`}
                      className="p-2 border rounded"
                      title="Apri email"
                    >
                      <Send size={17} />
                    </a>
                  )}

                  {r.telefono && (
                    <button
                      onClick={() => shareWhatsApp(r)}
                      className="p-2 border rounded"
                      title="Condividi su WhatsApp"
                    >
                      <MessageCircle size={17} />
                    </button>
                  )}
                </div>
              </div>

              <p className="text-sm whitespace-pre-wrap mt-3">
                {r.descrizione_lavori}
              </p>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold">Richiesta preventivo</h3>
              <button onClick={() => setOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <form
              className="p-5 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(form);
              }}
            >
              <div className="space-y-2">
                  <label className="block text-sm font-semibold">
                    Seleziona dall'Agenda Fornitori
                  </label>
                  <input
                    type="text"
                    value={ricercaFornitore}
                    onChange={(e) => setRicercaFornitore(e.target.value)}
                    placeholder="Cerca per ragione sociale o partita IVA..."
                    className="w-full border rounded px-3 py-2"
                  />
                  {ricercaFornitore.trim() && (
                    <div className="border rounded max-h-48 overflow-y-auto">
                      {caricamentoFornitori ? (
                        <p className="p-3 text-sm">Caricamento fornitori...</p>
                      ) : fornitoriFiltrati.length === 0 ? (
                        <p className="p-3 text-sm text-slate-500">
                          Nessun risultato. Puoi inserire i dati manualmente.
                        </p>
                      ) : (
                        fornitoriFiltrati.map((f, i) => (
                          <button
                            key={f.fornitore_id || i}
                            type="button"
                            onClick={() => selezionaFornitore(f)}
                            className="block w-full text-left px-3 py-2 hover:bg-slate-100 border-b"
                          >
                            <span className="font-semibold">{f.ragione_sociale}</span>
                            {f.partita_iva && (
                              <span className="text-xs text-slate-500 ml-2">
                                P.IVA {f.partita_iva}
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

              <label className="block text-sm font-semibold">
                Subappaltatore / Ragione sociale
                <input
                  required
                  className="mt-1 w-full border rounded p-2 font-normal"
                  value={form.ragione_sociale}
                  onChange={(e) =>
                    setForm({ ...form, ragione_sociale: e.target.value })
                  }
                />
              </label>

              <div className="grid md:grid-cols-2 gap-3">
                <label className="block text-sm font-semibold">
                  Email
                  <input
                    type="email"
                    className="mt-1 w-full border rounded p-2 font-normal"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                  />
                </label>

                <label className="block text-sm font-semibold">
                  Telefono / WhatsApp
                  <input
                    className="mt-1 w-full border rounded p-2 font-normal"
                    placeholder="Es. +39 333 1234567"
                    value={form.telefono}
                    onChange={(e) =>
                      setForm({ ...form, telefono: e.target.value })
                    }
                  />
                </label>
              </div>

              <label className="block text-sm font-semibold">
                Oggetto
                <input
                  required
                  className="mt-1 w-full border rounded p-2 font-normal"
                  value={form.oggetto}
                  onChange={(e) =>
                    setForm({ ...form, oggetto: e.target.value })
                  }
                />
              </label>

              <label className="block text-sm font-semibold">
                Descrizione lavori / Testo richiesta preventivo
                <textarea
                  required
                  rows={12}
                  className="mt-1 w-full border rounded p-3 font-normal"
                  placeholder="Descrivi liberamente i lavori da preventivare..."
                  value={form.descrizione_lavori}
                  onChange={(e) =>
                    setForm({ ...form, descrizione_lavori: e.target.value })
                  }
                />
              </label>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 border rounded"
                >
                  Annulla
                </button>

                <button
                  type="submit"
                  disabled={save.isPending}
                  className="px-4 py-2 bg-slate-800 text-white rounded disabled:opacity-50"
                >
                  {save.isPending ? "Salvataggio..." : "Salva richiesta"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
